// The reduced console for a free-wedge org.
//
// A free operator takes ONE wedge: visitor management or meeting rooms, never
// both. Everything else is the paid line. The full 191-item admin nav is the
// wrong shell for them: most of it demands a billing centre, a plan or a seat
// map they will never have, and landing in it makes a five-minute product feel
// like an ERP.
//
// Two rules this file exists to hold:
//
// 1. Paid modules stay VISIBLE and DISABLED, each with a one-line reason. No
//    dark patterns and no hiding. An operator should be able to see what the
//    paid product is without talking to anyone, and should never click into a
//    page that then tells them off.
// 2. The wedge items are real pages that work. Nothing here points at
//    something unbuilt.
//
// `planKind` comes off the user payload (`user.planKind`, from
// CoWorkingOrganization.plan_kind). Null means the full platform, and this
// file is not consulted at all.

import { PATH_ADMIN } from '../../../routes/paths';
import Iconify from '../../../components/Iconify';

// Same helper the full nav uses, so a wedge item and a paid item are drawn
// identically. Deliberately duplicated rather than exported from NavConfig:
// importing that file pulls in the whole 191-item tree.
const getIconfy = (name) => <Iconify icon={name} width={22} height={22} />;

// ----------------------------------------------------------------------

const WEDGE_ICONS = {
  visitors: getIconfy('mdi:account-group'),
  invites: getIconfy('mdi:email-fast-outline'),
  approvals: getIconfy('mdi:check-decagram-outline'),
  visitorSettings: getIconfy('mdi:tune-variant'),
  rooms: getIconfy('mdi:door-closed'),
  calendar: getIconfy('mdi:calendar-clock'),
  members: getIconfy('mdi:card-account-details-outline'),
  account: getIconfy('mdi:cog-outline'),
  // Locked items.
  lockedRooms: getIconfy('mdi:door-closed-lock'),
  lockedVisitors: getIconfy('mdi:account-lock-outline'),
  lockedSeats: getIconfy('mdi:seat-outline'),
  lockedBilling: getIconfy('mdi:file-document-outline'),
  lockedLeads: getIconfy('mdi:bullseye-arrow'),
  lockedHelpdesk: getIconfy('mdi:lifebuoy'),
  lockedAccess: getIconfy('mdi:key-variant'),
};

// One sentence, in the operator's language, saying what the thing is and that
// it is on the paid plan. Never a nag, never a countdown.
const REASON = {
  visitors: 'Visitor management is on the paid plan.',
  rooms: 'Meeting room booking is on the paid plan.',
  seats: 'Seats, plans and memberships are on the paid plan.',
  billing: 'Invoicing and payments are on the paid plan.',
  leads: 'The leads pipeline is on the paid plan.',
  helpdesk: 'Helpdesk and tickets are on the paid plan.',
  access: 'Door access and check-in are on the paid plan.',
};

const locked = (title, icon, reason) => ({
  roles: ['org'],
  title,
  icon,
  // No path: a locked item is never navigable. NavList reads `lockReason` and
  // refuses to route, so there is nowhere for a stray click to land.
  path: '',
  lockReason: reason,
  disabled: true,
});

// ----------------------------------------------------------------------
// Free visitor management
// ----------------------------------------------------------------------
const visitorsWedge = [
  {
    subheader: 'Front desk',
    items: [
      {
        roles: ['org'],
        title: 'Visitors',
        path: `${PATH_ADMIN.visitorManagement}?tab=visitors`,
        icon: WEDGE_ICONS.visitors,
        permission: 'visitors.view',
      },
      {
        roles: ['org'],
        title: 'Invites',
        path: `${PATH_ADMIN.visitorManagement}?tab=invitations`,
        icon: WEDGE_ICONS.invites,
        permission: 'visitors.view',
      },
      {
        roles: ['org'],
        title: 'Approvals',
        path: `${PATH_ADMIN.visitorManagement}?tab=approvals`,
        icon: WEDGE_ICONS.approvals,
        permission: 'visitors.view',
      },
    ],
  },
  {
    subheader: 'Setup',
    items: [
      {
        roles: ['org'],
        title: 'Visitor Settings',
        path: `${PATH_ADMIN.visitorManagement}?tab=settings`,
        icon: WEDGE_ICONS.visitorSettings,
        permission: 'visitors.view',
      },
      {
        roles: ['org'],
        title: 'Account',
        path: PATH_ADMIN.settings.general,
        icon: WEDGE_ICONS.account,
      },
    ],
  },
  {
    subheader: 'On the paid plan',
    items: [
      locked('Meeting Rooms', WEDGE_ICONS.lockedRooms, REASON.rooms),
      locked('Seats & Memberships', WEDGE_ICONS.lockedSeats, REASON.seats),
      locked('Invoices & Payments', WEDGE_ICONS.lockedBilling, REASON.billing),
      locked('Leads', WEDGE_ICONS.lockedLeads, REASON.leads),
      locked('Helpdesk', WEDGE_ICONS.lockedHelpdesk, REASON.helpdesk),
      locked('Door Access', WEDGE_ICONS.lockedAccess, REASON.access),
    ],
  },
];

// ----------------------------------------------------------------------
// Free meeting rooms
// ----------------------------------------------------------------------
const roomsWedge = [
  {
    subheader: 'Rooms',
    items: [
      {
        roles: ['org'],
        title: 'Meeting Rooms',
        path: PATH_ADMIN.meetingRoomManagement.root,
        icon: WEDGE_ICONS.rooms,
        permission: 'meeting_rooms.view',
      },
      {
        roles: ['org'],
        title: 'Members',
        // Members self-book, so member accounts are part of the free wedge:
        // without them the rooms have nobody to book them.
        path: PATH_ADMIN.members.root,
        icon: WEDGE_ICONS.members,
        permission: 'members.view',
      },
    ],
  },
  {
    subheader: 'Setup',
    items: [
      {
        roles: ['org'],
        title: 'Account',
        path: PATH_ADMIN.settings.general,
        icon: WEDGE_ICONS.account,
      },
    ],
  },
  {
    subheader: 'On the paid plan',
    items: [
      locked('Visitor Management', WEDGE_ICONS.lockedVisitors, REASON.visitors),
      locked('Seats & Memberships', WEDGE_ICONS.lockedSeats, REASON.seats),
      locked('Invoices & Payments', WEDGE_ICONS.lockedBilling, REASON.billing),
      locked('Leads', WEDGE_ICONS.lockedLeads, REASON.leads),
      locked('Helpdesk', WEDGE_ICONS.lockedHelpdesk, REASON.helpdesk),
      locked('Door Access', WEDGE_ICONS.lockedAccess, REASON.access),
    ],
  },
];

// ----------------------------------------------------------------------

const WEDGE_NAV = {
  visitors: visitorsWedge,
  rooms: roomsWedge,
};

// Where a wedge operator belongs when something hands them "the admin home".
// The dashboard is a portfolio view built out of occupancy, revenue and
// invoices, so for a wedge org it is a page of widgets for modules they do not
// have. Their home is the module they signed up for.
export const WEDGE_HOME = {
  visitors: `${PATH_ADMIN.visitorManagement}?tab=visitors`,
  rooms: PATH_ADMIN.meetingRoomManagement.root,
};

// Returns where to send a wedge operator, or '' for a full-platform org (which
// keeps the dashboard).
export function wedgeHome(planKind) {
  return WEDGE_HOME[planKind] || '';
}

// What the wedge is called when we have to name it to the operator.
export const WEDGE_LABEL = {
  visitors: 'Free Visitor Management',
  rooms: 'Free Meeting Rooms',
};

// Returns the reduced nav for a wedge org, or null for everyone else. Null is
// the signal to fall through to the full admin nav, so an unrecognised value
// can only ever show MORE than intended, never lock someone out of their own
// console.
export function getWedgeNav(planKind) {
  return WEDGE_NAV[planKind] || null;
}

export default getWedgeNav;
